import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  FileInput,
  Input,
  Select,
  Textarea,
} from "@/components";
import { axiosInstance } from "@/helpers/axios";
import styles from "./CreateCatalogModelDrawer.module.scss";

type AnimalOption = { slug: string; displayName: string };

type CreateCatalogModelDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  /** When set, species cannot be changed (e.g. model detail page). */
  lockedSpeciesSlug?: string;
  lockedSpeciesDisplayName?: string;
};

export const CreateCatalogModelDrawer = ({
  isOpen,
  onClose,
  onCreated,
  lockedSpeciesSlug,
  lockedSpeciesDisplayName,
}: CreateCatalogModelDrawerProps) => {
  const [animals, setAnimals] = useState<AnimalOption[]>([]);
  const [animalSlug, setAnimalSlug] = useState("");
  const [versionLabel, setVersionLabel] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [changeDescription, setChangeDescription] = useState("");
  const [sexFemale, setSexFemale] = useState(true);
  const [sexMale, setSexMale] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [resultVersionId, setResultVersionId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setChangeDescription("");
    setSexFemale(true);
    setSexMale(true);
    setFormError(null);
    setResultVersionId(null);
    setSubmitting(false);

    if (lockedSpeciesSlug) {
      setAnimalSlug(lockedSpeciesSlug);
      if (lockedSpeciesDisplayName) {
        setAnimals([
          { slug: lockedSpeciesSlug, displayName: lockedSpeciesDisplayName },
        ]);
      } else {
        setAnimals([
          { slug: lockedSpeciesSlug, displayName: lockedSpeciesSlug },
        ]);
        void (async () => {
          try {
            const { data } = await axiosInstance.get("/v2/models/catalog/animals");
            const list = (data?.data?.animals ?? []) as AnimalOption[];
            const found = list.find((a) => a.slug === lockedSpeciesSlug);
            if (found) {
              setAnimals([found]);
            }
          } catch {
            setAnimals([
              { slug: lockedSpeciesSlug, displayName: lockedSpeciesSlug },
            ]);
          }
        })();
      }
      return;
    }

    void (async () => {
      try {
        const { data } = await axiosInstance.get("/v2/models/catalog/animals");
        if (data?.success && data?.data?.animals?.length) {
          const list = data.data.animals as AnimalOption[];
          setAnimals(list);
          setAnimalSlug(list[0]?.slug ?? "");
        }
      } catch {
        setAnimals([]);
      }
    })();
  }, [isOpen, lockedSpeciesSlug, lockedSpeciesDisplayName]);

  const refreshNextVersion = async (slug: string) => {
    if (!slug) return;
    try {
      const { data } = await axiosInstance.get("/v2/models/catalog/next-version", {
        params: { speciesSlug: slug },
      });
      if (data?.success && data?.data?.suggestedVersionLabel) {
        setVersionLabel(data.data.suggestedVersionLabel as string);
      }
    } catch {
      setVersionLabel("v0.0.1");
    }
  };

  useEffect(() => {
    if (!isOpen || !animalSlug) return;
    void refreshNextVersion(animalSlug);
  }, [isOpen, animalSlug]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!animalSlug || !file) {
      setFormError("Choose an animal and a model file.");
      return;
    }
    const availableSexes: string[] = [];
    if (sexFemale) availableSexes.push("female");
    if (sexMale) availableSexes.push("male");
    if (availableSexes.length === 0) {
      setFormError("Select at least one available sex: Female and/or Male.");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("speciesSlug", animalSlug);
      if (versionLabel.trim()) {
        fd.append("versionLabel", versionLabel.trim());
      }
      if (changeDescription.trim()) {
        fd.append("changeDescription", changeDescription.trim());
      }
      fd.append("availableSexes", JSON.stringify(availableSexes));
      const { data } = await axiosInstance.post("/v2/models/versions", fd);
      if (!data?.success || !data?.data?.versionId) {
        setFormError(data?.message ?? "Could not create catalog version.");
        return;
      }
      setResultVersionId(data.data.versionId as string);
      await onCreated();
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "message" in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : "Upload failed.";
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Add catalog model version">
      <DrawerHeader
        title={resultVersionId ? "Version created" : "Add catalog model"}
        onClose={handleClose}
        closeDisabled={submitting}
      />
      {resultVersionId ? (
        <div className={styles["comp__stack"]}>
          <DrawerBody>
            <div className={styles["comp"]}>
              <div className={styles["comp__success"]}>
                <strong>New catalog version is queued for processing.</strong>
                <p className={styles["comp__url"]}>Version id: {resultVersionId}</p>
                <p className={styles["comp__expires"]}>
                  The origin file is stored on R2 (path{" "}
                  <code>{`models/<species>/<version>/<id>/…`}</code>), chunk outputs go under{" "}
                  <code>chunks/</code>, then the processor sets status to <code>draft</code>.
                  After processing, open <strong>Subscenes</strong> on the version to add detail
                  models linked to structures.
                </p>
              </div>
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button type="button" variant="primary" size="medium" onClick={handleClose}>
              Done
            </Button>
          </DrawerFooter>
        </div>
      ) : (
        <form className={styles["comp__stack"]} onSubmit={handleSubmit}>
          <DrawerBody>
            <div className={styles["comp"]}>
              <div className={styles["comp__fields"]}>
                <div className={styles["comp__row"]}>
                  <Select
                    id="create-catalog-animal"
                    label={
                      lockedSpeciesSlug
                        ? "Species (locked to this model)"
                        : "Species (slug)"
                    }
                    fullWidth
                    size="medium"
                    variant="secondary"
                    value={animalSlug}
                    disabled={submitting || Boolean(lockedSpeciesSlug)}
                    onChange={(v) => setAnimalSlug(String(v))}
                  >
                    {animals.map((a) => (
                      <option key={a.slug} value={a.slug}>
                        {a.displayName}
                      </option>
                    ))}
                  </Select>
                  <Input
                    id="create-catalog-version"
                    label="Version (semver)"
                    value={versionLabel}
                    placeholder="e.g. v1.0.0"
                    disabled={submitting}
                    onChange={setVersionLabel}
                  />
                </div>
                <div className={styles["comp__sexes"]}>
                  <span className={styles["comp__sexes-label"]} id="create-catalog-sexes-label">
                    Available sexes
                  </span>
                  <div className={styles["comp__sexes-row"]} role="group" aria-labelledby="create-catalog-sexes-label">
                    <Checkbox
                      id="create-catalog-sex-female"
                      checked={sexFemale}
                      disabled={submitting}
                      onChange={(v) => setSexFemale(v)}
                    >
                      Female
                    </Checkbox>
                    <Checkbox
                      id="create-catalog-sex-male"
                      checked={sexMale}
                      disabled={submitting}
                      onChange={(v) => setSexMale(v)}
                    >
                      Male
                    </Checkbox>
                  </div>
                  <p className={styles["comp__sexes-hint"]}>
                    Choose one or both. This is stored on the version and used by the app (same as catalog
                    metadata).
                  </p>
                </div>
                <FileInput
                  id="create-catalog-file"
                  label="Model file"
                  value={file}
                  onChange={setFile}
                  disabled={submitting}
                  variant="secondary"
                  fullWidth
                />
                <Textarea
                  id="create-catalog-description"
                  label="What changed (optional)"
                  placeholder="Short note for reviewers — fixes, new LODs, etc."
                  value={changeDescription}
                  disabled={submitting}
                  onChange={setChangeDescription}
                  variant="secondary"
                  size="medium"
                />
              </div>
              {formError ? <div className={styles["comp__error"]}>{formError}</div> : null}
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Uploading…" : "Create version"}
            </Button>
          </DrawerFooter>
        </form>
      )}
    </Drawer>
  );
};
